import {
  storage, storageRef, getDownloadURL, storageBaseUrl,
} from '../firebaseConfig.js';

/**
 * ONLY WORKS FOR PUBLIC FILES.
 *
 * Converts `shortPath` into a fully qualified firestore URL.
 * This is better than `getPrivateFirestoreUrl()` when possible because it is synchronous and
 * cheap (this method does not involve a call out to Firebase API).
 * 
 * @param {string} shortPath - Example: `/public/members/0KMst0UZ/profile-small.jpeg`
 * 
 * @returns {string} - Returns a fully qualified URL to the file. Returns empty string if
 *   `shortPath` is falsy.
 */
export const getPublicFirestoreUrl = (shortPath) => {
  if (!shortPath) return '';

  shortPath = shortPath.startsWith('/')
    ? shortPath.substring(1)
    : shortPath;

  const shortPath_encoded = encodeURIComponent(shortPath);
  return `${storageBaseUrl}/${shortPath_encoded}?alt=media`;
};

/**
 * Adds a `url` key to each level 2 object by passing the `path` key to `getPublicFirestoreUrl()`.
 * NOTE: If the passed `imageObject` is actually a string instead of an object, it will
 * be enriched directly assuming that it is a flattened firebase storage key.
 * 
 * @param {Object} imageObject - An image object from a firestore doc.
 * @returns {Object|String} - Returns the same object, but with a `url` key added to each level 2 
 *   object. Or returns a firestore enriched string if the passed value is a string.
 *   Note that if there is no `path` key for a particular value, the `url` key will not be added.
 */
export const enrichImageObject = (imageObject) => {
  if (!imageObject) return null;
  
  if (typeof imageObject === 'string') return getPublicFirestoreUrl(imageObject);
  
  if (typeof imageObject !== 'object') return null;

  return Object.fromEntries(
    Object.entries(imageObject).map(([key, imageFormatObject]) => {
      if (imageFormatObject?.path) {
        imageFormatObject.url = getPublicFirestoreUrl(imageFormatObject.path);
      }

      return [key, imageFormatObject];
    })
  );
}

/**
 * PREFERRED OPTION FOR PRIVATE FILES, BUT WORKS FOR PUBLIC FILES AS WELL
 *
 * Converts `shortPath` into a fully qualified firestore URL. Queries the Firestore API and
 * uses getDownloadURL(), which generates an authenticated link.
 * 
 * Note that if any errors occur they will be captured and logged to the console and a null
 * value will be returned.
 * 
 * @param {string} shortPath - Example: `private/members/0M3eHvTC/agreements/dlHb9FRW.pdf`
 */
/** Returns a promise that resolves to the current token. */
export const getPrivateFirestoreUrl = async (shortPath) => {
  const fileRef = storageRef(storage, shortPath);

  try {
    return await getDownloadURL(fileRef);
  } catch (error) {
    console.error(
      `getPrivateFirestoreUrl: Error getting private URL for ${shortPath}. ${error.message}`
    );
    return null;
  }
};
